import { getActivityMixFromDesign } from '@shared/helpers/design/activity-mix.helpers';
import { getGeographicalAdjustmentFactor } from '@shared/helpers/energy/city-climate.helpers';
import {
  getEnergyClass,
  getEnergyConsumptionRecordFromBuildingVersion,
  getMaxAllowedPrimaryEnergyValue,
  getPrimaryEnergyValues,
} from '@shared/helpers/energy/energy-class';
import { sum } from '@shared/helpers/math.helpers';
import type { Baseline } from '@shared/models/project.model';
import type { EnergyClass } from '@shared/helpers/energy/energy.models';
import {
  resolveHeatedAreaForBoverketExport,
  type BoverketHeatedAreaInput,
} from './boverket-climate-declaration-heated-area.helpers';

import type { IProposal } from '@shared/models/proposals.model';

export type BoverketEnergiklassInput = BoverketHeatedAreaInput & {
  version?: Baseline;
  buildingLifetime?: number;
  proposal?: IProposal;
};

/**
 * Boverket energiklass (A–G) from design geometry/activities and version B6 energy.
 * Matches {@link getEnergyClass} usage in project insights / operational energy report.
 */
export const getEnergiklassForBoverketExport = ({
  design,
  version,
  buildingLifetime = 1,
  versionExpressionVariables,
  projectMeta,
}: BoverketEnergiklassInput): EnergyClass | '' => {
  if (!version) return '';

  const heatedArea = resolveHeatedAreaForBoverketExport({
    design,
    versionExpressionVariables,
    projectMeta,
  });
  if (heatedArea <= 0) return '';

  const activityMix = getActivityMixFromDesign(design);
  const geoFactor = getGeographicalAdjustmentFactor(design.location);
  const energyConsumptionRecord = getEnergyConsumptionRecordFromBuildingVersion(
    version,
    buildingLifetime,
  );
  const primaryEnergyValue = sum(
    getPrimaryEnergyValues(energyConsumptionRecord, heatedArea, geoFactor),
  );
  const maxAllowedPrimaryEnergyValue = getMaxAllowedPrimaryEnergyValue(
    heatedArea,
    activityMix,
  );

  return getEnergyClass(primaryEnergyValue, maxAllowedPrimaryEnergyValue);
};
